// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategory from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategory";
import { getPageLinkByNodeId, LinkModel } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { css } from "styled-components";
import iconsObject from "@stoneage/XDL/Icons/AllIcons";
import { trackAsImpersonation, trackGaToolbarNavClickEvent } from "@stoneage/App/Utilities/DataLayer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { connect } from "react-redux";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import useGetHeaderLinkList from "@stoneage/App/Hooks/useGetHeaderLinkList";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import FloatingMenuTooltip from "@stoneage/App/Components/Menu/FloatingMenuTooltip";
import { FloatingTooltipStyles } from "@stoneage/App/Components/FloatingTooltip/FloatingTooltip";
import { HeaderLinkListFieldModel } from "@stoneage/App/Types/headerLinkList";

export interface ResolvedHeaderLink {
    link?: { url?: string; title?: string };
    overriddenTitle?: string;
    icon?: string;
    isMenu?: boolean;
    openInNewWindow?: boolean;
    excludeFromNavigation?: boolean;
    children?: {
        id?: string;
        url?: string;
        title?: string;
        overriddenTitle?: string;
        sortOrder?: number;
        excludeFromNavigation?: boolean;
        hideFromSearchEngines?: boolean;
        openInNewWindow?: boolean;
    }[];
}

const enum fields {
    links = "links",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: HeaderLinkListFieldModel[];
        [fields.customCSS]: string;
    };
    extendedStyles?: HeaderLinkListStyles;
}

export interface HeaderLinkListStyles {
    headerLinkListWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    // XNG-Changes
    floatingTooltip?: FloatingTooltipStyles;
    menuItemLink?: LinkPresentationProps;
    iconLink?: IconPresentationProps;
    arrowIcon?: IconPresentationProps;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    return {
        impersonation: trackAsImpersonation(state),
    };
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export const headerLinkListStyles: HeaderLinkListStyles = {
    headerLinkListWrapper: {
        css: css`
            text-align: right;
            padding: 0 0 23px 0;
            margin-right: -9px;
        `,
    },
    link: {
        typographyProps: {
            variant: "headerTertiary",
            css: css`
                margin: 0 9px 0 9px;
                display: inline-block;
                &&& {
                    color: ${getColor("text.main")};
                }
            `,
        },
    },
};

const defaultCustomCss = `.list-wrapper {
}

.list-link {
}
`;

export const HeaderLinkList: React.FC<Props> = ({ impersonation, fields, extendedStyles }) => {
    const links = useGetHeaderLinkList(fields.links);
    const styles = useMergeStyles("headerLinkList", headerLinkListStyles, extendedStyles);

    if (links.length < 1) {
        return <StyledWrapper>{/* To make sure language appear on left side */}</StyledWrapper>;
    }

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="list-wrapper" {...styles.headerLinkListWrapper}>
                {links.map((linkItem: ResolvedHeaderLink, index: number) => {
                    if (!linkItem) {
                        return null;
                    }

                    const { link, icon, isMenu, openInNewWindow, overriddenTitle, excludeFromNavigation, children } =
                        linkItem;

                    const displayTitle = overriddenTitle || link?.title || "";

                    const menuHasAtLeastOneNavigationalItem =
                        isMenu && children?.length ? children.some(child => !child.excludeFromNavigation) : false;

                    if (
                        link?.url &&
                        !excludeFromNavigation &&
                        (!isMenu || (isMenu && !menuHasAtLeastOneNavigationalItem))
                    ) {
                        return (
                            <StyledWrapper key={`link${link.url}.${link.title}${index}`}>
                                <Link
                                    href={link.url}
                                    target={openInNewWindow ? "_blank" : ""}
                                    onClick={() => trackGaToolbarNavClickEvent(displayTitle, impersonation)}
                                    {...styles.link}
                                >
                                    <Icon src={icon} {...styles.iconLink} />
                                    {displayTitle}
                                </Link>
                            </StyledWrapper>
                        );
                    }

                    // Menu link — with navigational children
                    if (isMenu && children?.length) {
                        return (
                            <StyledWrapper
                                className="header_links-popup"
                                key={`link${link?.url}.${link?.title}${index}`}
                            >
                                <FloatingMenuTooltip
                                    triggerComponent={
                                        <Link
                                            href={!excludeFromNavigation ? link?.url : ""}
                                            target={openInNewWindow ? "_blank" : ""}
                                            arial-role="menu"
                                            onClick={() => {
                                                trackGaToolbarNavClickEvent(displayTitle, impersonation);
                                            }}
                                            {...styles.link}
                                        >
                                            <Icon src={icon} {...styles.iconLink} />
                                            {displayTitle}
                                            <Icon
                                                src="ChevronDown"
                                                className="arrow-icon"
                                                aria-label="expand"
                                                {...styles.arrowIcon}
                                            />
                                        </Link>
                                    }
                                    showArrow={false}
                                    placement="bottom-end"
                                    offset={{ mainAxis: 0 }}
                                    extendedStyles={styles.floatingTooltip}
                                >
                                    {children.map((subItem, subIndex) => {
                                        if (subItem.excludeFromNavigation) {
                                            return null;
                                        }

                                        const subDisplayTitle = subItem.overriddenTitle || subItem.title || "";
                                        return (
                                            <StyledWrapper key={`menuSub_${subItem.url}_${subItem.title}_${subIndex}`}>
                                                <Link
                                                    {...styles.menuItemLink}
                                                    href={subItem.url}
                                                    color="text.main"
                                                    onClick={() => {
                                                        trackGaToolbarNavClickEvent(subDisplayTitle, impersonation);
                                                    }}
                                                >
                                                    {subDisplayTitle}
                                                </Link>
                                            </StyledWrapper>
                                        );
                                    })}
                                </FloatingMenuTooltip>
                            </StyledWrapper>
                        );
                    }

                    return null;
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

// XNG-Changes
const stoneAgeIcons = Object.keys(iconsObject).map(iconKey => ({ value: iconKey, displayName: iconKey })) || [];

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(HeaderLinkList),
    definition: {
        group: "Header",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    const { overriddenTitle, title } = item.fields;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overriddenTitle || link?.title;
                    }
                    if (type === "Category") {
                        if (overriddenTitle) {
                            return overriddenTitle;
                        }
                        if (value === emptyGuid) {
                            return "Products";
                        }
                        const categoryState = getCategoryState(state, value);
                        if (!categoryState.value) {
                            if (!categoryState.isLoading && value) {
                                return dispatch => {
                                    dispatch(
                                        loadCategory({
                                            id: value,
                                        }),
                                    );
                                };
                            }
                            return "";
                        }
                        return categoryState.value.shortDescription;
                    }
                    if (type === "Url") {
                        return overriddenTitle || title || value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    // XNG-Changes
                    {
                        name: "overriddenTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                    },
                    // XNG-Changes
                    {
                        name: "icon",
                        displayName: "Icon",
                        editorTemplate: "DropDownField",
                        options: stoneAgeIcons,
                        defaultValue: "",
                        isRequired: false,
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    // XNG-Changes
                    {
                        name: "isMenu",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    // XNG-Changes
                    {
                        name: "l2Links",
                        displayName: "SubLinks",
                        editorTemplate: "ListField",
                        tooltip: "if the link authored is showing a menu, you need to author the links below.",
                        isVisible: item => item.fields.isMenu === true,
                        getDisplay: (item: HasFields, state) => {
                            const { type, value } = item.fields.destination;
                            const { overriddenTitle, title } = item.fields;
                            if (type === "Page") {
                                const link = getPageLinkByNodeId(state, value);
                                return overriddenTitle || link?.title;
                            }
                            if (type === "Category") {
                                if (overriddenTitle) {
                                    return overriddenTitle;
                                }
                                if (value === emptyGuid) {
                                    return "Products";
                                }
                                const categoryState = getCategoryState(state, value);
                                if (!categoryState.value) {
                                    if (!categoryState.isLoading && value) {
                                        return dispatch => {
                                            dispatch(
                                                loadCategory({
                                                    id: value,
                                                }),
                                            );
                                        };
                                    }
                                    return "";
                                }
                                return categoryState.value.shortDescription;
                            }
                            if (type === "Url") {
                                return title ?? value;
                            }
                            return value;
                        },
                        defaultValue: [],
                        fieldDefinitions: [
                            {
                                name: "destination",
                                editorTemplate: "LinkField",
                                defaultValue: {
                                    value: "",
                                    type: "Page",
                                },
                                isRequired: true,
                            },
                            {
                                name: "overriddenTitle",
                                editorTemplate: "TextField",
                                defaultValue: "",
                            },
                            {
                                name: "openInNewWindow",
                                editorTemplate: "CheckboxField",
                                defaultValue: false,
                            },
                        ],
                    },
                    // XNG-Changes
                    {
                        name: "excludeFromNavigation",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
